import { useState, useCallback, ReactNode, FC, Key, useMemo, useContext } from "react";
import { notification, Tooltip } from "@findemdev/maui";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { decodeString } from "@findemdev/findem-helpers";
// styled comp + components
import { CredentialName, StyledTable, TableWrapper } from "./CredentialsTable.styled";
import DeleteCredential from "../../Modals/DeleteCredential/DeleteCredential";
import CreateUpdateCredential from "../../Modals/CreateUpdateCredential";
// types
import { CredentialLibraryType, CredRef, ICredential } from "../../../types/credential";
import { ISearchSortFilter } from "../../../../../types/shared";
import { UpdateCredentialHandler } from "../../../types/common";
import type { ColumnsType, TableProps } from 'antd/es/table';
// api
import { matchesApi, selectIsToggleFavoriteCredPending, useCloneCredentialMutation, useDeleteCredMutation, useToggleMarqueeCredentialMutation } from "../../../../../services/matches";
// custom hooks
import useUpdateCredential from "../../Modals/CreateUpdateCredential/hooks/useUpdateCredential";
// context
// util
import { CREDENTIALS_PER_PAGE, QueryParams } from "../../../util/constants";
import { formatLastModifiedDate } from "../../../util/common";
import { useAppSelector } from "../../../hooks/storeHooks";
import Favorite from "./Favorite";
import useGetCredentialJoid from "../../../hooks/useGetCredentialJoid";
import { TabId } from "../NavigationTabs";
import CopyCredential from "../../Modals/CopyCredential/CopyCredential";
import { useFetchAllUsersQuery } from "../../../../../services/payments";
import UserContext from "../../../../../contexts/UserContext";
import Owner from "./Owner";
import MarqueeToggle from "../../Modals/MarqueeToggle/MarqueeToggle";
import { FullStory }  from '@fullstory/browser';
import { CredBuilderLibraryEvents, FullStoryCustomEvents } from "../../../../../utils/fullstory";

interface ICredentialsTableProps {
  data: ICredential[]; //credentials data
  total: number;
  isPending: boolean;
  activeTab: TabId;
  pageNumber: number;
  pageChange: (page: number, pageSize: number) => void;
  handleSorting: (sortObj: ISearchSortFilter | undefined) => void;
}

interface IRow {
  key: string;
  name: ReactNode;
  type: string;
  companyName: string;
  rraIndustry: string;
  rraPractice: string;
  capability: string;
  owner: ReactNode;
  favorite: ReactNode;
  lastModifiedDate: string;
}

export interface EditDetailsModal {
  show: boolean;
  credential: ICredential | null;
};

const CredentialsTable: FC<ICredentialsTableProps> = ({
  data,
  total,
  isPending,
  activeTab,
  pageNumber,
  pageChange,
  handleSorting,
}) => {
  // store
  const dispatch = useDispatch();
  const userModel = useContext(UserContext);

  // api
  const toggleFavoriteCredApi = useAppSelector(selectIsToggleFavoriteCredPending);
  const [searchParams] = useSearchParams();

  const [deleteCred, { isLoading: isDeleteCredPending }] = useDeleteCredMutation();
  const [cloneCredential, { isLoading: isCloneCredentialPending }] = useCloneCredentialMutation();
  const [toggleMarqueeCredential, { isLoading: isToggleMarqueeCredentialPending }] = useToggleMarqueeCredentialMutation();
  const { data: allUsers, isFetching: isFetchingAllUsers } = useFetchAllUsersQuery({ joid: userModel.getUser().uid });

  const [deleteCredModal, setDeleteCredModal] = useState<{
    show: boolean;
    credRef: CredRef | null;
  }>({
    show: false,
    credRef: null
  });
  const [editDetailsModal, setEditDetailsModal] = useState<EditDetailsModal>({
    show: false,
    credential: null
  });
  const [copyCredModal, setCopyCredModal] = useState<{
    show: boolean;
    newCredName: string;
    credential: ICredential | null;
  }>({
    show: false,
    newCredName: "",
    credential: null
  });
  const [marqueeCredModal, setMarqueeCredModal] = useState<{
    show: boolean;
    credential: ICredential | null;
  }>({
    show: false,
    credential: null
  });

  const navigate = useNavigate();

  const effectiveCredentialFromModalAction = editDetailsModal.credential ?? copyCredModal.credential ?? marqueeCredModal.credential;
  // custom hook
  const { updateCredential } = useUpdateCredential({ credential: effectiveCredentialFromModalAction ?? undefined });
  const credentialJoid = useGetCredentialJoid({});

  const [_selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const _handleCheckboxClick = useCallback(
    (newSelectedRowKeys: Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    []
  );

  // on cred click go to cred details with path /my-apps/cred-builder/credentials/{credId}
  const handleCredClick = useCallback(
    (credId: string) => {
      navigate(`credentials/${credId}?${searchParams.toString()}`);
    },
    [navigate, searchParams]
  );

  const columns: ColumnsType<IRow> = useMemo(
    () => [
      {
        title: 'Credential Name',
        dataIndex: "name",
        key: "name",
        className: "cred-name",
        sorter: true,
        sortKey: "name",
      },
      // @deprecated - https://findemjira.atlassian.net/browse/RRINT-1839
      // {
      //   title: "Type",
      //   dataIndex: "type",
      //   key: "type",
      //   sorter: true,
      //   sortKey: "type",
      // },
      {
        title: "Company Name",
        dataIndex: "companyName",
        key: "companyName",
        sorter: true,
        sortKey: "client_company_name",
      },
      {
        title: "RRA Industry",
        dataIndex: "rraIndustry",
        key: "rraIndustry",
        sorter: true,
        sortKey: "industry",
      },
      {
        title: "RRA Practice",
        dataIndex: "rraPractice",
        key: "rraPractice",
        sorter: true,
        sortKey: "practice",
      },
      {
        title: "Capability",
        dataIndex: "capability",
        key: "capability",
        sorter: true,
        sortKey: "capability",
      },
      ...(activeTab === TabId['cred-library'] || activeTab === TabId['marquees'] || activeTab === TabId['favorites'])
        ? [{
            title: "Owner",
            dataIndex: "owner",
            key: "owner",
          }]
        : [],
      {
        title: "Favorites",
        dataIndex: "favorite",
        key: "favorite",
        sorter: true,
        sortKey: "favorite_marked_by",
      },
      {
        title: "Last Modified Date",
        dataIndex: "lastModifiedDate",
        key: "lastModifiedDate",
      },
    ],
    [activeTab]
  );

  const dataSource: IRow[] = useMemo(() => {
    if (data && data.length > 0) {
      return data.map((credential) => {
        const decodedCredentialName = decodeString(credential.name);

        return {
          key: credential.ref,
          name: <Tooltip title={decodedCredentialName}>
            <CredentialName>
              {decodedCredentialName}
            </CredentialName>
          </Tooltip>,
          type: credential?.type ?? "-",
          companyName: credential?.client_company_name ?? "-",
          rraPractice: credential?.practice ?? "-",
          rraIndustry: credential?.industry?.join(", ") ?? "-",
          capability: credential?.capability ?? "-",
          owner: (activeTab === TabId['cred-library'] || activeTab === TabId['marquees'] || activeTab === TabId['favorites']) ? <Owner
            credentialOwnerUid={credential.uid}
            isPending={isFetchingAllUsers}
            allUsers={allUsers}
          /> : null,
          favorite: <Favorite
            credential={credential}
            activeTab={activeTab}
            setDeleteCredModal={setDeleteCredModal}
            setEditDetailsModal={setEditDetailsModal}
            setCopyCredModal={setCopyCredModal}
            setMarqueeCredModal={setMarqueeCredModal}
          />,
          lastModifiedDate: formatLastModifiedDate({ updatedAt: credential.updated_at }),
        }
      });
    }
    return [];
  }, [data, activeTab, isFetchingAllUsers, allUsers]);

  const handleDelete = useCallback(async () => {
    try {
      const ref = deleteCredModal.credRef!

      await deleteCred({
        ref,
        joid: credentialJoid
      }).unwrap();

      notification.success({
        "message": "Successfully deleted the Credential"
      });

      setDeleteCredModal({
        credRef: null,
        show: false
      });
    } catch (error) {
      console.error("Error while deleting Credential: ", error);
      notification.error({
        "message": "Failed to delete the Credential"
      });
    }
  }, [deleteCredModal.credRef, credentialJoid]);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteCredModal({
      show: false,
      credRef: null
    });
  }, []);

  const handleCloseEditDetailsModal = useCallback(() => {
    setEditDetailsModal({
      show: false,
      credential: null
    });
  }, []);

  const handleTableChange = useCallback<
    NonNullable<TableProps<IRow>['onChange']>
  >((_pagination, _filters, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const column = activeSorter?.column as { sortKey: string } | undefined;

    handleSorting(column ? {
      field: column.sortKey,
      order: activeSorter?.order === 'ascend' ? 'asc' : 'desc'
    } : undefined);
  }, [handleSorting]);

  const createCredentialHandler: UpdateCredentialHandler = useCallback(async ({
    name,
    client_company_name,
    industry,
    practice,
    capability,
    libraries,
    onEnd,
  })  => {
    if (!editDetailsModal.credential) return;
      await updateCredential({
        title: name,
        credential: {
          ...editDetailsModal.credential,
          client_company_name,
          industry,
          practice,
          capability,
          libraries,
        },
        onSuccess: () => {
          notification.success({
            message: "Success",
            description: "Credential saved successfully",
          });
          setEditDetailsModal({
            show: false,
            credential: null
          });
          dispatch(matchesApi.util.invalidateTags(["Credentials"]));
        },
        onError: () => {
          notification.error({
            message: "Error",
            description: "Failed to update credential",
          });
        },
        onEnd,
      });
  }, [editDetailsModal.credential, updateCredential]);

  const handleMakeCopy = async ({ newCredName }: { newCredName: string }) => {
    const resp = await cloneCredential({
      joid: credentialJoid,
      name: newCredName,
      ref: copyCredModal.credential!.ref,
    }).unwrap();
    if (resp.data) {
      // track fullstory event for cloning credential
      FullStory('trackEvent', {
        name: FullStoryCustomEvents.CredentialBuilder,
        properties: {
          event: CredBuilderLibraryEvents.CredentialCloned,
          sourceCredentialId: copyCredModal.credential!.ref,
          newCredentialId: resp.data?.ref,
          homeTab: searchParams.get(QueryParams.HomeTab),
        }
      });
      navigate(`credentials/${resp.data?.ref}?${searchParams.toString()}`);
    }
  };

  const onCloseCopyModal = () => {
    setCopyCredModal({ show: false, credential: null, newCredName: "" });
  }

  const onCloseMarqueeModal = () => {
    setMarqueeCredModal({ show: false, credential: null });
  }

  const handleToggleMarquee = async () => {
    if (!marqueeCredModal.credential) return;
    try {
      const isMarqueeCred = marqueeCredModal.credential.libraries?.includes(CredentialLibraryType.MARQUEE_LIBRARY);
      await toggleMarqueeCredential({
        joid: marqueeCredModal.credential.uid,
        ref: marqueeCredModal.credential.ref,
        library: marqueeCredModal.credential.libraries ?? [],
      }).unwrap();
      onCloseMarqueeModal();
      // track event for toggling marquee status
      FullStory('trackEvent', {
        name: FullStoryCustomEvents.CredentialBuilder,
        properties: {
          event: isMarqueeCred ? CredBuilderLibraryEvents.RemovedFromMarquee : CredBuilderLibraryEvents.AddedToMarquee,
          credentialId: marqueeCredModal.credential.ref,
          loggedInUserId: userModel.getUser().uid,
          homeTab: searchParams.get(QueryParams.HomeTab),
        }
      });
      notification.success({
        "message": isMarqueeCred ? "Unmarked as a Marquee credential" : "Marked as a Marquee credential",
        "description": isMarqueeCred ? "This credential is no longer marked as a Marquee example." : "This credential is now marked as a Marquee example."
      });
    } catch (error) {
      console.error("Error while toggling marquee status: ", error);
      notification.error({
        "message": "Failed to update the Marquee status of the Credential"
      });
      return;
    }
  }

  return (
    <TableWrapper>
      {
        <StyledTable<IRow>
          scroll={{ y: 'calc(100vh - 420px)' }}
          columns={columns}
          dataSource={dataSource}
          sortDirections={['ascend', 'descend']}
          onChange={handleTableChange}
          pagination={{
            current: pageNumber + 1,
            onChange: pageChange,
            pageSize: CREDENTIALS_PER_PAGE,
            total: total,
          }}
          onRow={(record) => {
            return {
              onClick: (e) => {
                if (e.target instanceof HTMLElement) {
                  const classList = Array.from(e.target.classList);

                  // action dropdown menu clicked
                  if (classList.some((className) => className.startsWith('credential-action'))) {
                    return;
                  }
                }
                handleCredClick(record.key);
              }
            }
          }}
          loading={isPending || isDeleteCredPending || toggleFavoriteCredApi.isLoading}
          rowClassName="cred-list-rows"
        />
      }
      {deleteCredModal.show &&
        <DeleteCredential
          open={deleteCredModal.show}
          isDeleteCredPending={isDeleteCredPending}
          handleCloseDeleteModal={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      }
      {editDetailsModal.show && editDetailsModal.credential && (
        <CreateUpdateCredential
          credential={editDetailsModal.credential}
          onClose={handleCloseEditDetailsModal}
          onGenerate={createCredentialHandler}
          onCreateManually={() => { }}
        />
      )}
      {copyCredModal.show && copyCredModal.credential &&
        <CopyCredential
          newCredName={copyCredModal.newCredName}
          open={copyCredModal.show}
          onClose={onCloseCopyModal}
          isCopyPending={isCloneCredentialPending}
          handleMakeCopy={handleMakeCopy}
        />
      }
      {marqueeCredModal.show && marqueeCredModal.credential &&
        <MarqueeToggle
          open={marqueeCredModal.show}
          credential={marqueeCredModal.credential}
          isPending={isToggleMarqueeCredentialPending}
          onOk={handleToggleMarquee}
          onCancel={onCloseMarqueeModal}
        />
      }
      {marqueeCredModal.show && marqueeCredModal.credential &&
        <MarqueeToggle
          open={marqueeCredModal.show}
          credential={marqueeCredModal.credential}
          isPending={isToggleMarqueeCredentialPending}
          onOk={handleToggleMarquee}
          onCancel={onCloseMarqueeModal}
        />
      }
    </TableWrapper>
  );
};

export default CredentialsTable;
